/* eslint-disable max-lines -- Pre-existing violation */
import {match} from 'ts-pattern';
import {z} from 'zod';

import {FEATURE_CATEGORIES} from '@dms/features/new-vehicle';

import type {Nullish} from 'shared/web';

import {
  findKeyOwner,
  isModelKeyOfMake,
  resolveMakeIdentity,
  resolveModelIdentity,
} from './catalogueIdentity';
import {dateWindowRefinement, invalidDateMessage, isCalendarDate, t} from './catalogueUi';
import {colourSchema} from './colourSchema';
import {engineSchema} from './engineSchema';
import {equipmentDraftSchema} from './equipmentDraft';
import {optionalMoneyInput, priceInput} from './moneyInput';
import {trimSchema} from './trimSchema';
import {trimWindow} from './trimWindow';
import {upholsterySchema} from './upholsterySchema';
import {trimPriceRequired} from './utils/offeredTrimRows';

const identity = z.object({id: z.string().min(1)});
const engine = engineSchema.extend({
  ...identity.shape,
  transmissionCode: z.string(),
  transmission: z.string().trim().min(1),
  drive: z.string().trim().min(1),
});
const trimRow = trimSchema.extend({
  id: z.string(),
  bodyStyle: z.string().trim().min(1),
  factoryCode: z.string(),
  costNet: optionalMoneyInput,
});
const trim = trimRow.superRefine(trimWindow);
const trimPrices = z.record(z.string(), z.string());
const colour = colourSchema.pick({name: true, kind: true, factoryCode: true, hex: true}).extend({
  ...identity.shape,
  trimPrices,
  costNet: optionalMoneyInput,
  trimIds: z.array(z.string()),
});
const upholstery = upholsterySchema.extend({
  ...identity.shape,
  costNet: optionalMoneyInput,
  trimIds: z.array(z.string()),
});
const variant = z.object({
  id: z.string(),
  trimId: z.string(),
  engineId: z.string(),
  orderCode: z.string().trim().min(1),
  priceNet: priceInput,
  costNet: optionalMoneyInput,
});
const VARIANT_FIELD_ISSUES = [
  {
    field: 'orderCode',
    key: 'entity.newVehicle.authoring.variantOrderCodeRequired',
    fallback: '{trim}: enter the order code for {engines}.',
  },
  {
    field: 'priceNet',
    key: 'entity.newVehicle.authoring.variantPriceInvalid',
    fallback: '{trim}: enter a decimal price such as 120.00 for {engines}.',
  },
  {
    field: 'costNet',
    key: 'entity.newVehicle.authoring.variantCostInvalid',
    fallback: '{trim}: enter a decimal cost such as 120.00 for {engines}, or leave it empty.',
  },
] as const;
const attachment = z.object({
  id: z.string(),
  featureId: z.string(),
  trimId: z.string(),
  inclusion: z.enum(['STANDARD', 'OPTIONAL']),
  priceNet: z.string(),
});
const equipment = equipmentDraftSchema.omit({trimPrices: true}).extend({
  ...identity.shape,
  libraryId: z.string().nullable(),
});
const libraryEquipmentReference = identity.extend({libraryId: z.string().min(1)});
const packageDraft = z.object({
  id: z.string(),
  name: z.string().trim().min(1),
  factoryCode: z.string(),
  description: z.string().trim().min(1),
  trimPrices,
  costNet: optionalMoneyInput,
  memberFeatureIds: z.array(z.string()),
  applicableTrimIds: z.array(z.string()),
});

// Local author intent, not an API DTO. Incomplete rows must survive reload; review validates them.
export const newModelDraftSchema = z.object({
  schemaVersion: z.literal(3),
  make: z.object({
    mode: z.enum(['existing', 'new']),
    id: z.string(),
    name: z.string(),
    hsn: z.string(),
    /**
     * Vehicle catalogue make: the key a new make is created with, or the key an existing unlinked
     * make gets linked to on Create. '' leaves the make as the tenant's own.
     */
    vehicleDataKey: z.string().default(''),
    /** The author chose to create this make although an existing one has a similar name. */
    keepSeparate: z.boolean().default(false),
  }),
  model: z.object({
    mode: z.enum(['existing', 'new']),
    id: z.string(),
    name: z.string(),
    tsn: z.string(),
    modelFamily: z.string(),
    /** Vehicle catalogue model family of the make's key; created with, or linked on Create. */
    vehicleDataKey: z.string().default(''),
    /** The author chose to create this model although an existing one has a similar name. */
    keepSeparate: z.boolean().default(false),
  }),
  year: z.object({
    code: z.string(),
    catalogueName: z.string(),
    pricelistVersion: z.string(),
    // The date field emits null when cleared; '' is only the untouched default.
    validFrom: z.string().nullable(),
    validTo: z.string().nullable(),
  }),
  engines: z.array(
    engine.extend({
      name: z.string(),
      fuel: z.string(),
      /** Drafts saved before hybrid types existed have none. */
      hybridType: z.string().default(''),
      powerKw: z.string(),
      powerPs: z.string(),
      displacementCcm: z.string(),
      transmission: z.string(),
      drive: z.string(),
    })
  ),
  trims: z.array(
    trimRow.extend({
      name: z.string(),
      bodyStyle: z.string(),
      validFrom: z.string().nullable(),
      validTo: z.string().nullable(),
      basePriceNet: z.string(),
      costNet: z.string(),
    })
  ),
  colours: z.array(colour.extend({name: z.string(), hex: z.string(), costNet: z.string()})),
  upholsteries: z.array(
    upholstery.extend({
      name: z.string(),
      // Drafts saved before upholstery had a colour carry no hex.
      hex: z.string().default(''),
      priceNet: z.string(),
      costNet: z.string(),
    })
  ),
  variants: z.array(
    variant.extend({orderCode: z.string(), priceNet: z.string(), costNet: z.string()})
  ),
  equipment: z.array(
    equipment.extend({
      name: z.string(),
      category: z.union([z.enum(FEATURE_CATEGORIES), z.literal('')]),
      costNet: z.string(),
    })
  ),
  attachments: z.array(attachment),
  packages: z.array(
    packageDraft.extend({
      name: z.string(),
      description: z.string(),
      costNet: z.string(),
    })
  ),
  restrictions: z.array(z.object({colourId: z.string(), upholsteryId: z.string()})),
  // Older persisted drafts (schemaVersion 1) only carry {status, modelYearId}.
  materialization: z.object({
    status: z.enum(['not-started', 'started', 'complete']),
    modelYearId: z.string().nullable(),
    makeId: z.string().nullable().optional(),
    modelId: z.string().nullable().optional(),
    /** Draft row id → created server id; variants use `${trimRowId}:${engineRowId}`. */
    created: z.record(z.string(), z.string()).default({}),
    pendingCreate: z.string().nullable().optional(),
    lastError: z.string().nullable().optional(),
    /** The backend refused this row's code as already used in the model year. */
    codeConflict: z
      .object({
        rowId: z.string(),
        code: z.string(),
        message: z.string(),
      })
      .nullable()
      .optional(),
  }),
});
export type NewModelDraft = z.infer<typeof newModelDraftSchema>;
export type Materialization = NewModelDraft['materialization'];
export type DraftIssue = {step: number; message: string};
export const NOT_STARTED: Materialization = {
  status: 'not-started',
  modelYearId: null,
  created: {},
  pendingCreate: null,
};

export const restrictionLabel = (
  draft: NewModelDraft,
  restriction: NewModelDraft['restrictions'][number]
) =>
  t('entity.newVehicle.authoring.restrictedPair', '{colour} × {upholstery} not allowed', {
    colour: draft.colours.find(({id}) => id === restriction.colourId)?.name ?? restriction.colourId,
    upholstery:
      draft.upholsteries.find(({id}) => id === restriction.upholsteryId)?.name ??
      restriction.upholsteryId,
  });
export const newModelDraftKey = (tenantId: string, userId: string) =>
  `newVehicleAuthoring:v1:${tenantId}:${userId}`;
export const variantKey = (trimRowId: string, engineRowId: string) => `${trimRowId}:${engineRowId}`;

/**
 * Rows whose code the backend keeps unique in the model year, by materialization key, with the step
 * and listed record (a variant's trim) that edit them.
 */
export const codedDraftRows = (draft: NewModelDraft) => [
  ...draft.engines.map((engine) => ({
    step: 4,
    key: engine.id,
    recordId: engine.id,
    code: engine.factoryCode,
  })),
  ...draft.variants.map((variant) => ({
    step: 5,
    key: variantKey(variant.trimId, variant.engineId),
    recordId: variant.trimId,
    code: variant.orderCode,
  })),
  ...draft.colours.map((colour) => ({
    step: 6,
    key: colour.id,
    recordId: colour.id,
    code: colour.factoryCode,
  })),
];
/**
 * The code the backend refused while its row still holds it. A changed code is left to the next
 * attempt: the backend stays the judge of duplicates.
 */
export function draftCodeConflict(draft: NewModelDraft) {
  const conflict = draft.materialization.codeConflict;
  const row = codedDraftRows(draft).find(({key}) => key === conflict?.rowId);
  return conflict && row?.code === conflict.code
    ? {...conflict, step: row.step, recordId: row.recordId}
    : undefined;
}

type DraftCollection = 'engines' | 'trims' | 'colours' | 'upholsteries' | 'equipment' | 'packages';

export function emptyNewModelDraft(): NewModelDraft {
  return {
    schemaVersion: 3,
    make: {mode: 'existing', id: '', name: '', hsn: '', vehicleDataKey: '', keepSeparate: false},
    model: {
      mode: 'new',
      id: '',
      name: '',
      tsn: '',
      modelFamily: '',
      vehicleDataKey: '',
      keepSeparate: false,
    },
    year: {code: '', catalogueName: '', pricelistVersion: '', validFrom: '', validTo: ''},
    engines: [],
    trims: [],
    colours: [],
    upholsteries: [],
    variants: [],
    equipment: [],
    attachments: [],
    packages: [],
    restrictions: [],
    materialization: NOT_STARTED,
  };
}

export function newModelSteps() {
  return [
    t('entity.vehicle.labels.make', 'Make'),
    t('entity.vehicle.labels.model', 'Model'),
    t('entity.newVehicle.labels.modelYear', 'Model year'),
    t('entity.newVehicle.catalogue.engines', 'Engines'),
    t('entity.newVehicle.catalogue.trimLevels', 'Trim levels'),
    t('entity.newVehicle.authoring.colors', 'Colours'),
    t('entity.newVehicle.catalogue.upholstery', 'Upholstery'),
    t('entity.newVehicle.catalogue.equipment', 'Equipment'),
    t('entity.newVehicle.catalogue.packages', 'Packages'),
    t('entity.newVehicle.authoring.review', 'Review'),
  ];
}
/**
 * Whether the author has put anything into a step yet.
 *
 * Every required step is "blocked" before it is filled in, so blockers alone cannot tell a step
 * the author broke from one they simply have not reached. The stepper uses this to stay quiet
 * about untouched steps and only raise the ones worth going back to.
 */
export function stepHasContent(draft: NewModelDraft, step: number): boolean {
  const anyFilled = (...values: (string | null | undefined)[]) =>
    values.some((value) => (value ?? '').trim().length > 0);
  switch (step) {
    case 1:
      return draft.make.mode === 'existing'
        ? anyFilled(draft.make.id)
        : anyFilled(draft.make.name, draft.make.hsn, draft.make.vehicleDataKey);
    case 2:
      return draft.model.mode === 'existing'
        ? anyFilled(draft.model.id)
        : anyFilled(
            draft.model.name,
            draft.model.tsn,
            draft.model.modelFamily,
            draft.model.vehicleDataKey
          );
    case 3:
      return anyFilled(
        draft.year.code,
        draft.year.catalogueName,
        draft.year.pricelistVersion,
        draft.year.validFrom,
        draft.year.validTo
      );
    case 4:
      return draft.engines.length > 0;
    case 5:
      return draft.trims.length > 0 || draft.variants.length > 0;
    case 6:
      return draft.colours.length > 0;
    case 7:
      return draft.upholsteries.length > 0;
    case 8:
      return draft.equipment.length > 0 || draft.attachments.length > 0;
    case 9:
      return draft.packages.length > 0;
    default:
      return false;
  }
}
/**
 * Display label per row id; rows sharing a name are numbered in draft order so their messages stay
 * apart, skipping numbers whose label another row already carries as its name.
 */
const distinctLabels = (rows: {id: string; name: string}[], fallback: string) => {
  const names = rows.map((row) => row.name || fallback);
  const taken = new Set(names);
  return new Map(
    rows.map((row, index) => {
      const name = names[index];
      if (names.indexOf(name) === names.lastIndexOf(name)) {
        return [row.id, name];
      }
      let position = 1;
      while (taken.has(`${name} (${position})`)) {
        position += 1;
      }
      taken.add(`${name} (${position})`);
      return [row.id, `${name} (${position})`];
    })
  );
};
/** The model-year date fields; text typed into one that is not a date reaches the draft as null. */
export type YearDateField = 'validFrom' | 'validTo';

/** A colour's or package's price on each trim it is chosen on, as rows; a trim without one has ''. */
export const trimPriceRows = (trimIds: string[], trimPrices: Partial<Record<string, string>>) =>
  trimIds.map((trimId) => ({trimId, priceNet: trimPrices[trimId] ?? ''}));

// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export function reviewNewModelDraft(
  draft: NewModelDraft,
  invalidDateInputs: readonly YearDateField[] = []
): DraftIssue[] {
  const issues: DraftIssue[] = [];
  // Generic per-row messages (e.g. removed references) carry no row identity; list each once.
  const add = (step: number, key: string, fallback: string, options?: Record<string, unknown>) => {
    const message = t(key, fallback, options);
    if (!issues.some((issue) => issue.step === step && issue.message === message)) {
      issues.push({step, message});
    }
  };
  if (!(draft.make.mode === 'existing' ? draft.make.id : (draft.make.name ?? '').trim())) {
    add(
      1,
      'entity.newVehicle.authoring.requireMake',
      'Select an existing make or enter a new make name.'
    );
  }
  if (
    !(draft.model.mode === 'existing'
      ? draft.model.id && draft.make.mode === 'existing'
      : (draft.model.name ?? '').trim())
  ) {
    add(
      2,
      'entity.newVehicle.authoring.requireModel',
      'Select a model under the chosen make or enter a new model name.'
    );
  }
  if (!(draft.year.code ?? '').trim() || !(draft.year.catalogueName ?? '').trim()) {
    add(
      3,
      'entity.newVehicle.authoring.requireYear',
      'Enter the OEM model-year code and catalogue name.'
    );
  }
  if (!(draft.year.pricelistVersion ?? '').trim()) {
    add(3, 'entity.newVehicle.authoring.requirePricelistVersion', 'Enter the price list version.');
  }
  const isInvalidDate = (field: YearDateField) => {
    const value = draft.year[field];
    return invalidDateInputs.includes(field) || (!!value && !isCalendarDate(value));
  };
  if (isInvalidDate('validFrom')) {
    issues.push({
      step: 3,
      message: `${t('entity.newVehicle.authoring.validFrom', 'Valid from')}: ${invalidDateMessage()}`,
    });
  } else if (!draft.year.validFrom) {
    issues.push({
      step: 3,
      message: `${t('entity.newVehicle.authoring.validFrom', 'Valid from')}: ${t('general.validations.fieldIsRequired', 'Field is required')}`,
    });
  }
  if (isInvalidDate('validTo')) {
    issues.push({
      step: 3,
      message: `${t('entity.newVehicle.authoring.validTo', 'Valid to')}: ${invalidDateMessage()}`,
    });
  }
  z
    .object({validFrom: z.string().nullable(), validTo: z.string().nullable()})
    .superRefine(dateWindowRefinement('validFrom', 'validTo'))
    .safeParse(draft.year)
    .error?.issues.forEach(({message}) =>
      issues.push({
        step: 3,
        message: `${t('entity.newVehicle.authoring.validTo', 'Valid to')}: ${message}`,
      })
    );
  const trimLabels = distinctLabels(
    draft.trims,
    t('entity.newVehicle.authoring.unnamedTrim', 'Unnamed trim')
  );
  const engineLabels = distinctLabels(
    draft.engines,
    t('entity.newVehicle.authoring.unnamedEngine', 'Unnamed engine')
  );
  // Row refinements carry their own localised message; structural failures fall back to the generic hint.
  const rowIssues = (
    step: number,
    row: {name: string},
    schema: z.ZodTypeAny,
    label = row.name || newModelSteps()[step - 1]
  ) => {
    const result = schema.safeParse(row);
    if (result.success) {
      return;
    }
    const specific = result.error.issues.filter(
      (issue) => issue.code === z.ZodIssueCode.custom && issue.message !== 'Invalid input'
    );
    for (const issue of specific) {
      issues.push({step, message: `${label}: ${issue.message}`});
    }
    if (specific.length < result.error.issues.length) {
      issues.push({
        step,
        message: `${label}: ${t('entity.newVehicle.authoring.checkFields', 'complete required fields and use decimal prices such as 120.00.')}`,
      });
    }
  };
  const rows = [
    {step: 4, items: draft.engines, schema: engine},
    {step: 5, items: draft.trims, schema: trim},
    {step: 6, items: draft.colours, schema: colour},
    {step: 7, items: draft.upholsteries, schema: upholstery},
    {step: 9, items: draft.packages, schema: packageDraft},
  ];
  for (const group of rows) {
    if (group.step < 8 && group.items.length === 0) {
      issues.push({
        step: group.step,
        message: `${newModelSteps()[group.step - 1]}: ${t('entity.newVehicle.authoring.addAtLeastOne', 'add at least one record.')}`,
      });
    }
    for (const row of group.items) {
      rowIssues(
        group.step,
        row,
        group.schema,
        group.step === 5 ? trimLabels.get(row.id) : undefined
      );
    }
  }
  for (const row of draft.equipment) {
    rowIssues(8, row, row.libraryId === null ? equipment : libraryEquipmentReference);
  }
  for (const row of draft.trims) {
    const trimVariants = draft.variants.filter((v) => v.trimId === row.id);
    if (!trimVariants.length) {
      issues.push({
        step: 5,
        message: `${trimLabels.get(row.id)}: ${t('entity.newVehicle.authoring.requireDrive', 'select at least one engine.')}`,
      });
    }
    // One message per trim and field, listing its engines, so N broken variants stay one line.
    for (const {field, key, fallback} of VARIANT_FIELD_ISSUES) {
      const engines = draft.engines
        .filter((draftEngine) =>
          trimVariants.some(
            (v) =>
              v.engineId === draftEngine.id && !variant.shape[field].safeParse(v[field]).success
          )
        )
        .map((draftEngine) => engineLabels.get(draftEngine.id));
      if (engines.length) {
        add(5, key, fallback, {trim: trimLabels.get(row.id), engines: engines.join(', ')});
      }
    }
  }
  if (
    draft.variants.some(
      (row) =>
        !draft.trims.some((t) => t.id === row.trimId) ||
        !draft.engines.some((e) => e.id === row.engineId)
    )
  ) {
    add(
      5,
      'entity.newVehicle.authoring.variantReferenceInvalid',
      'A trim × engine variant refers to a removed trim or engine.'
    );
  }
  for (const row of [...draft.colours, ...draft.upholsteries]) {
    if (!row.trimIds.length || row.trimIds.some((id) => !draft.trims.some((t) => t.id === id))) {
      issues.push({
        step: draft.colours.some((c) => c.id === row.id) ? 6 : 7,
        message: `${row.name}: ${t('entity.newVehicle.authoring.requireTrim', 'select at least one available trim.')}`,
      });
    }
  }
  const pricedOnTrims = [
    ...draft.colours.map((row) => ({step: 6, row, trimIds: row.trimIds})),
    ...draft.packages.map((row) => ({step: 9, row, trimIds: row.applicableTrimIds})),
  ];
  for (const {step, row, trimIds} of pricedOnTrims) {
    if (trimIds.some((id) => !priceInput.safeParse(row.trimPrices[id] ?? '').success)) {
      issues.push({step, message: `${row.name}: ${trimPriceRequired()}`});
    }
  }
  for (const row of draft.attachments) {
    const feature = draft.equipment.find((e) => e.id === row.featureId);
    if (
      !feature ||
      !draft.trims.some((t) => t.id === row.trimId) ||
      (!feature.standaloneOrderable && row.inclusion === 'OPTIONAL')
    ) {
      add(
        8,
        'entity.newVehicle.authoring.invalidAttachment',
        'Equipment has an invalid trim reference or a package-only standalone selection.'
      );
    }
    if (feature && row.inclusion === 'OPTIONAL' && !priceInput.safeParse(row.priceNet).success) {
      add(
        8,
        'entity.newVehicle.authoring.libraryOptionPriceRequired',
        '{name}: open Edit and enter its Optional price.',
        {name: feature.name}
      );
    }
  }
  for (const row of draft.packages) {
    if (
      !row.memberFeatureIds.length ||
      !row.applicableTrimIds.length ||
      row.memberFeatureIds.some((id) => !draft.equipment.some((e) => e.id === id)) ||
      row.applicableTrimIds.some((id) => !draft.trims.some((t) => t.id === id))
    ) {
      issues.push({
        step: 9,
        message: `${row.name}: ${t('entity.newVehicle.authoring.requirePackageLinks', 'choose existing equipment members and applicable trims.')}`,
      });
    }
  }
  for (const row of draft.restrictions) {
    if (
      !draft.colours.some((c) => c.id === row.colourId) ||
      !draft.upholsteries.some((u) => u.id === row.upholsteryId)
    ) {
      add(
        7,
        'entity.newVehicle.authoring.invalidRestriction',
        'A colour × upholstery restriction refers to a removed record.'
      );
    }
  }
  const codeConflict = draftCodeConflict(draft);
  if (codeConflict) {
    issues.push({step: codeConflict.step, message: codeConflict.message});
  }
  return issues;
}

type CatalogueIdentityRecord = {
  id: string;
  name: string;
  vehicleDataKey?: string | Nullish;
  archived?: boolean;
};
type CatalogueModelIdentityRecord = CatalogueIdentityRecord & {makeId: string};

/** Key of the make the model goes under: the chosen record's own, its link, or a new make's. */
export const draftMakeVehicleDataKey = (draft: NewModelDraft, makes: CatalogueIdentityRecord[]) =>
  (draft.make.mode === 'existing'
    ? makes.find((make) => make.id === draft.make.id)?.vehicleDataKey || draft.make.vehicleDataKey
    : draft.make.vehicleDataKey) || '';

/** Draft make for an existing record, optionally linked to a catalogue key on Create. */
export const existingDraftMake = (id: string, linkKey = ''): NewModelDraft['make'] => ({
  mode: 'existing',
  id,
  name: '',
  hsn: '',
  vehicleDataKey: linkKey,
  keepSeparate: false,
});

/** Draft model for an existing record, optionally linked to a catalogue key on Create. */
export const existingDraftModel = (
  draft: NewModelDraft,
  id: string,
  linkKey = ''
): NewModelDraft['model'] => ({
  ...draft.model,
  mode: 'existing',
  id,
  name: '',
  vehicleDataKey: linkKey,
  keepSeparate: false,
});

/** Replaces the draft's make; a model pick or catalogue model tied to the previous make is dropped. */
export const withDraftMake = (
  draft: NewModelDraft,
  make: NewModelDraft['make'],
  model: Partial<NewModelDraft['model']> = {}
): NewModelDraft => ({
  ...draft,
  make,
  model: {...draft.model, id: '', vehicleDataKey: '', keepSeparate: false, ...model},
});

/**
 * How the make and model the draft names relate to the tenant's catalogue: for a new record, the
 * existing one it duplicates or resembles; for an existing record being linked, another record
 * already holding that key. What an earlier attempt of this draft created is its own record.
 */
export function draftCatalogueIdentity(
  draft: NewModelDraft,
  makes: CatalogueIdentityRecord[],
  models: CatalogueModelIdentityRecord[]
) {
  const {makeId, modelId} = draft.materialization;
  const make =
    draft.make.mode === 'new'
      ? resolveMakeIdentity(
          makes.filter((record) => record.id !== makeId),
          draft.make
        )
      : {similar: []};
  const model =
    draft.model.mode === 'new' && draft.make.mode === 'existing'
      ? resolveModelIdentity(
          models.filter((record) => record.id !== modelId),
          draft.make.id,
          draft.model
        )
      : {similar: []};
  const linkedMake = draft.make.mode === 'existing' && draft.make.vehicleDataKey;
  const linkedModel =
    draft.model.mode === 'existing' && draft.make.mode === 'existing' && draft.model.vehicleDataKey;
  return {
    make: {
      ...make,
      keyOwner: linkedMake
        ? findKeyOwner(makes, draft.make.vehicleDataKey, draft.make.id)
        : undefined,
    },
    model: {
      ...model,
      keyOwner: linkedModel
        ? findKeyOwner(
            models.filter((record) => record.makeId === draft.make.id),
            draft.model.vehicleDataKey,
            draft.model.id
          )
        : undefined,
    },
  };
}

const identityIssue = (
  entity: 'make' | 'model',
  identity: ReturnType<typeof draftCatalogueIdentity>['make'],
  keepSeparate: boolean
) => {
  const {match, similar, keyOwner} = identity;
  const name = match?.record.name ?? keyOwner?.name ?? similar[0]?.name ?? '';
  if (keyOwner) {
    return entity === 'make'
      ? t(
          'entity.newVehicle.authoring.makeKeyTaken',
          '{name} is already linked to this vehicle catalogue make. Use {name} instead.',
          {name}
        )
      : t(
          'entity.newVehicle.authoring.modelKeyTaken',
          '{name} is already linked to this vehicle catalogue model. Use {name} instead.',
          {name}
        );
  }
  if (match?.kind === 'archived') {
    return entity === 'make'
      ? t(
          'entity.newVehicle.authoring.makeNameArchived',
          'An archived make is already called {name}. Choose a different name.',
          {name}
        )
      : t(
          'entity.newVehicle.authoring.modelNameArchived',
          'An archived model of this make is already called {name}. Choose a different name.',
          {name}
        );
  }
  if (match) {
    return entity === 'make'
      ? t(
          'entity.newVehicle.authoring.makeAlreadyExists',
          '{name} is already in the catalogue. Use the existing make instead of creating it again.',
          {name}
        )
      : t(
          'entity.newVehicle.authoring.modelAlreadyExists',
          '{name} is already a model of this make. Use the existing model instead of creating it again.',
          {name}
        );
  }
  if (similar.length && !keepSeparate) {
    return entity === 'make'
      ? t(
          'entity.newVehicle.authoring.makeSimilarUndecided',
          'Decide whether {name} is this make: link it, or create a new make.',
          {name}
        )
      : t(
          'entity.newVehicle.authoring.modelSimilarUndecided',
          'Decide whether {name} is this model: link it, or create a new model.',
          {name}
        );
  }
  return undefined;
};

/**
 * Blockers that need the tenant's catalogue: a new make or model the backend would refuse as a
 * duplicate (names ignore letter case and include archived records), a link to a key another
 * record holds, an undecided look-alike, and a catalogue model outside the make's catalogue entry.
 */
export function reviewDraftCatalogueIdentity(
  draft: NewModelDraft,
  makes: CatalogueIdentityRecord[],
  models: CatalogueModelIdentityRecord[]
): DraftIssue[] {
  const identity = draftCatalogueIdentity(draft, makes, models);
  const issues: DraftIssue[] = [];
  const makeIssue = identityIssue('make', identity.make, draft.make.keepSeparate);
  if (makeIssue) {
    issues.push({step: 1, message: makeIssue});
  }
  const modelIssue = identityIssue('model', identity.model, draft.model.keepSeparate);
  if (modelIssue) {
    issues.push({step: 2, message: modelIssue});
  }
  if (
    draft.model.vehicleDataKey &&
    !isModelKeyOfMake(draft.model.vehicleDataKey, draftMakeVehicleDataKey(draft, makes))
  ) {
    issues.push({
      step: 2,
      message: t(
        'entity.newVehicle.authoring.catalogueModelOfOtherMake',
        'The vehicle catalogue model belongs to a different make. Choose it again for this make.'
      ),
    });
  }
  return issues;
}

export function removeDraftRecord(
  draft: NewModelDraft,
  collection: DraftCollection,
  id: string
): NewModelDraft {
  const next = {
    ...draft,
    engines: draft.engines.filter((row) => collection !== 'engines' || row.id !== id),
    trims: draft.trims.filter((row) => collection !== 'trims' || row.id !== id),
    colours: draft.colours
      .filter((row) => collection !== 'colours' || row.id !== id)
      .map((row) => ({
        ...row,
        trimIds: row.trimIds.filter((ref) => collection !== 'trims' || ref !== id),
      })),
    upholsteries: draft.upholsteries
      .filter((row) => collection !== 'upholsteries' || row.id !== id)
      .map((row) => ({
        ...row,
        trimIds: row.trimIds.filter((ref) => collection !== 'trims' || ref !== id),
      })),
    equipment: draft.equipment
      .filter((row) => collection !== 'equipment' || row.id !== id)
      .map((row) => ({
        ...row,
        trimIds: row.trimIds.filter((ref) => collection !== 'trims' || ref !== id),
      })),
    packages: draft.packages
      .filter((row) => collection !== 'packages' || row.id !== id)
      .map((row) => ({
        ...row,
        memberFeatureIds: row.memberFeatureIds.filter(
          (ref) => collection !== 'equipment' || ref !== id
        ),
        applicableTrimIds: row.applicableTrimIds.filter(
          (ref) => collection !== 'trims' || ref !== id
        ),
      })),
    variants: draft.variants.filter(
      (row) =>
        !(collection === 'trims' && row.trimId === id) &&
        !(collection === 'engines' && row.engineId === id)
    ),
    attachments: draft.attachments.filter(
      (row) =>
        !(collection === 'trims' && row.trimId === id) &&
        !(collection === 'equipment' && row.featureId === id)
    ),
    restrictions: draft.restrictions.filter(
      (row) =>
        !(collection === 'colours' && row.colourId === id) &&
        !(collection === 'upholsteries' && row.upholsteryId === id)
    ),
  };
  return next;
}

const NULLABLE_IDENTITY_FIELDS = new Set([
  'libraryId',
  'modelYearId',
  'makeId',
  'modelId',
  'pendingCreate',
  'lastError',
  'codeConflict',
]);
const storedRecord = z.record(z.string(), z.unknown());
// Drafts saved before the closed category enum hold free text; reset it so review asks for a group.
const storedCategory = z.enum(FEATURE_CATEGORIES).or(z.literal('')).catch('');
/**
 * Drafts saved before the vehicle catalogue codelists hold free text such as 'petrol' or 'SUV'.
 * A value without the catalogue key prefix is reset so review asks the author to pick it again; the
 * prefix is a structural check only, the backend validates the key itself.
 */
const storedCatalogueKey = (prefix: string) =>
  z
    .string()
    .catch('')
    .transform((value) => (value.startsWith(prefix) ? value : ''));
const migrateCatalogueKeys = (rows: unknown, prefixes: Record<string, string>) => {
  const parsed = z.array(storedRecord).safeParse(rows);
  return parsed.success
    ? parsed.data.map((row) => ({
        ...row,
        ...Object.fromEntries(
          Object.entries(prefixes).map(([field, prefix]) => [
            field,
            storedCatalogueKey(prefix).parse(row[field]),
          ])
        ),
      }))
    : undefined;
};
const priceOnEachTrim = (rows: unknown, trimIdsField: string) =>
  z
    .array(storedRecord)
    .safeParse(rows)
    .data?.map((row) => {
      const trimIds = z.array(z.string()).catch([]).parse(row[trimIdsField]);
      const price = z.string().catch('').parse(row.priceNet);
      return {...row, trimPrices: Object.fromEntries(trimIds.map((id) => [id, price]))};
    });
function migrateStoredDraft(value: unknown) {
  const draft = storedRecord.safeParse(value);
  if (!draft.success) {
    return value;
  }
  const equipment = z.array(storedRecord).safeParse(draft.data.equipment);
  const engines = migrateCatalogueKeys(draft.data.engines, {
    fuel: 'FUELTYPE_',
    hybridType: 'HYBRIDTYPE_',
    transmission: 'TRANSMISSION_',
    drive: 'DRIVE_',
  });
  const trims = migrateCatalogueKeys(draft.data.trims, {bodyStyle: 'CARSTYLE_'});
  const isItemPriced = z.number().int().min(1).max(2).safeParse(draft.data.schemaVersion).success;
  const colours = migrateCatalogueKeys(
    isItemPriced ? priceOnEachTrim(draft.data.colours, 'trimIds') : draft.data.colours,
    {kind: 'COLORSPECIFICATION_'}
  );
  const packages = isItemPriced
    ? priceOnEachTrim(draft.data.packages, 'applicableTrimIds')
    : undefined;
  const upholsteries = migrateCatalogueKeys(draft.data.upholsteries, {
    fabric: 'INTERIORMATERIAL_',
  });
  const isRowPriced = draft.data.schemaVersion === 1;
  const rowPrice = (featureId: unknown) => {
    const row = equipment.data?.find(({id}) => id === featureId);
    const isLibraryCopy = typeof row?.libraryId === 'string';
    return !isLibraryCopy && typeof row?.defaultPriceNet === 'string' ? row.defaultPriceNet : '';
  };
  const attachments = z.array(storedRecord).safeParse(draft.data.attachments);
  return {
    ...draft.data,
    ...(isItemPriced ? {schemaVersion: 3} : {}),
    ...(isRowPriced && attachments.success
      ? {
          attachments: attachments.data.map((row) => ({
            ...row,
            priceNet: row.inclusion === 'OPTIONAL' ? rowPrice(row.featureId) : '',
          })),
        }
      : {}),
    ...(engines ? {engines} : {}),
    ...(trims ? {trims} : {}),
    ...(colours ? {colours} : {}),
    ...(packages ? {packages} : {}),
    ...(upholsteries ? {upholsteries} : {}),
    ...(equipment.success
      ? {
          equipment: equipment.data.map((row) => {
            // The picker is locked for wheel sets, so they must never resume without wheels_tyres.
            const category =
              row.equipmentType === 'wheel' ? 'wheels_tyres' : storedCategory.parse(row.category);
            if (row.equipmentType === 'package-only') {
              return {
                ...row,
                category,
                equipmentType: 'factory-option',
                standaloneOrderable: false,
              };
            }
            return 'standaloneOrderable' in row
              ? {...row, category}
              : {...row, category, standaloneOrderable: true};
          }),
        }
      : {}),
  };
}
const storedDraftSchema = z.object({
  step: z.number().int().min(1).max(10),
  savedAt: z.number().int().positive().optional(),
  draft: z.preprocess(migrateStoredDraft, newModelDraftSchema),
});
export function readNewModelDraft(key: string) {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(key);
  } catch {
    return {status: 'unavailable'} as const;
  }
  if (raw === null) {
    return {status: 'empty'} as const;
  }
  try {
    // Platform controls emitted null for cleared text in early drafts. Preserve nullable
    // server identities; the structural schema still rejects malformed objects and enums.
    const parsed = storedDraftSchema.safeParse(
      JSON.parse(raw, (field: string, value: unknown) =>
        value === null && !NULLABLE_IDENTITY_FIELDS.has(field) ? '' : value
      )
    );
    return (
      match(parsed)
        .with({success: false}, () => ({status: 'invalid'}) as const)
        // Drafts stored before completion cleared them are already in the catalogue.
        .with(
          {success: true, data: {draft: {materialization: {status: 'complete'}}}},
          () => ({status: 'empty'}) as const
        )
        .with({success: true}, ({data}) => ({status: 'saved', ...data}) as const)
        .exhaustive()
    );
  } catch {
    return {status: 'invalid'} as const;
  }
}
export function saveNewModelDraft(key: string, draft: NewModelDraft, step: number) {
  try {
    // Toggling a radio or leaving an empty step is not work worth resuming, so an untouched
    // wizard never leaves a draft behind for the catalogue banner or the resume prompt. Neither
    // is a draft already created in the catalogue: resuming it only invites a second copy.
    const hasContent =
      draft.materialization.status === 'started' ||
      (draft.materialization.status === 'not-started' &&
        newModelSteps().some((_, index) => stepHasContent(draft, index + 1)));
    if (!hasContent) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify({step, savedAt: Date.now(), draft}));
    }
    return true;
  } catch {
    return false;
  }
}
